import React, { ReactElement, useEffect, useRef, useState } from 'react';
import './CreateJobLogTable.scss';
import {
	CallFieldMappingConfig,
	CallLogData,
	CallModeDirectionCallFieldMappingConfig,
	MultiFieldModeParticipantNameCallFieldMappingConfig,
	ParticipantNameCallFieldMappingConfig,
	ParticipantNameMappingFormat
} from '../../store/state/createJobState';
import classNames from 'classnames';
import errorIcon from '../../imgs/icons/error.svg';
import warningIcon from '../../imgs/icons/warning.svg';
import checkmarkBlueIcon from '../../imgs/icons/checkmark-blue.svg';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/actions/appDispatch';
import {
	clearCallFieldMapping,
	clearCallFieldMappingByColumnIndex,
	updateCallFieldMappingConfig
} from '../../store/actions/createJobActions';
import MappingConfigurationModal, { MappingConfigurationModalData } from './MappingConfigurationModal';
import {
	CallFieldMappingItemResult,
	CallFieldMappingResult,
	getCallFieldMappingResult,
	getCallFieldMappingValidationResultsByMappingId,
	getColumnIndexesForMappingConfig,
	getErrorCallFieldValidations,
	getWarningCallFieldValidations
} from '../../store/selectors/createJobSelectors';
import Tooltip from '../Tooltip';
import CreateJobRequiredFieldsText from './CreateJobRequiredFieldsText';
import CreateJobDirectionSummary from './CreateJobDirectionSummary';
import MappingResultPreview from '../MappingResultPreview';
import CustomSelect, { CustomSelectOption, CustomSelectProps } from '../CustomSelect';
import { MappingRequirementType } from '../../models/mappingRequirementType';

const maxRowsToRender = 50;
const tableCellClass = 'create-job-log-table__cell';

enum ParticipantNamePart {
	FirstOnly = 'FirstOnly',
	LastOnly = 'LastOnly'
}

type ParticipantNameColumnType = ParticipantNamePart | ParticipantNameMappingFormat;

export default function CreateJobLogTable(props: Props) {
	const dispatch = useDispatch<AppDispatch>();
	const [configData, setConfigData] = useState<MappingConfigurationModalData | null>(null);
	const headerRowRef = useRef<HTMLTableRowElement | null>(null);
	const [headerRowHeight, setHeaderRowHeight] = useState<number | null>(null);
	const subHeaderTopStyles = getSubHeaderTopStyles(headerRowHeight);
	const callRowsToRender = props.callLogData.callRows.slice(0, maxRowsToRender)
	const rowsAreTruncated = props.callLogData.callRows.length > maxRowsToRender;
	const validationsResultsByMappingId = getCallFieldMappingValidationResultsByMappingId(props.mappings, props.callLogData);
	
	useEffect(() => {
		const win = window as any;
		
		if (headerRowRef.current && win.ResizeObserver) {
			try {
				const elm = headerRowRef.current;
				const observer = new win.ResizeObserver(() => {
					if (elm === headerRowRef.current) {
						setHeaderRowHeight(elm.offsetHeight);
					} else {
						// this shouldn't really happen as far as I'm aware
						// but checking just in case. this would be like if 
						// the element got torn down and re-created
						setHeaderRowHeight(null);
					}
				});

				// initialize observing
				observer.observe(elm);

				// initialize elm height
				setHeaderRowHeight(elm.offsetHeight);

				return () => observer.disconnect();
			} catch(e) {
				console.log('Error setting up resize observer');
			}
		}
	}, []);
	
	if (!props.callLogData.callRows.length)
		return <div className="text-center text-italic">No data</div>;
	
	const columnHeaders = getColumnHeaders(props.callLogData.callRows[0].length);
	const previewColumnMap = getPreviewColumnMap();
	const mappingsByColIndex = getMappingsByColIndex();
	const directionMapping = props.mappings.find(m => m.type === 'direction');
	const mappingOptions = getMappingSelectOptions();
	
	return (
		<React.Fragment>
			<div className="create-job-log-table__container">
				<div className="create-job-log-table__banner-section">
					<div className="create-job-log-table__banner">
						<CreateJobRequiredFieldsText mappings={props.mappings} /> 
						<span className="text-light create-job-log-table__banner-item-divider"> | </span>
						{!!directionMapping && 
							<React.Fragment>
								<CreateJobDirectionSummary mapping={directionMapping}
														   callLogData={props.callLogData}
														   descriptive={true}
														   onEditConfiguration={() => openCallDirectionModal(directionMapping)}
														   onRemoveConfiguration={() => dispatch(clearCallFieldMapping(props.engagementId, directionMapping.id))} />
								
							</React.Fragment>
						}
					</div>
				</div>
				<div className="create-job-log-table__table-section">
					<div className="create-job-log-table__table-container">
						<table className="create-job-log-table__table">
							<tbody>
							<tr ref={headerRowRef} className="create-job-log-table__row create-job-log-table__row_sticky">
								{renderRowItems(columnHeaders, previewColumnMap, (header, colIndex) => {
									const mapping = mappingsByColIndex.get(colIndex);
									const validationResult = mapping ? validationsResultsByMappingId.get(mapping.id) : null;
									const errors = validationResult ? getErrorCallFieldValidations(validationResult, props.showNonIntrusiveErrors).filter(v => v.sourceColumnIndex == null || v.sourceColumnIndex == colIndex).map(v => v.message) : [];
									const warnings = validationResult ? getWarningCallFieldValidations(validationResult).filter(v => v.sourceColumnIndex == null || v.sourceColumnIndex == colIndex).map(v => v.message) : [];
									const customSelectProps: CustomSelectProps<MappingSelectOption> = {
										selection: mappingOptions.find(o => isMappingOptionSelectedForColumn(o, mapping, colIndex)),
										onSelectionChange: (option) => onColumnSelection(colIndex, option),
										options: mappingOptions,
										getOptionLabel: option => option.label,
										getOptionId: option => option.id,
										isOptionDisabled: option =>isMappingOptionDisabledForColumn(option, mapping, colIndex),
										ariaLabel: `Choose Creovai field for ${header}`,
										closeOnScrollTargetClassName: 'create-job-log-table__table-container'
									};
									
									return (
										<td className={classNames(tableCellClass, 'create-job-log-table__cell_primary-header')}
											key={colIndex}>
											<div>
												<div className="create-job-log-table__mapping-select-container">
													<CustomSelect { ...customSelectProps } />
												</div>
												{!!errors.length && <Tooltip renderContent={() => renderValidationTooltipContent(errors)} placement="bottom" fallbackPlacements={['bottom-start', 'bottom-end']}><img src={errorIcon} tabIndex={0} aria-label={errors.join(', ')} alt="Error" className="create-job-log-table__header-error-icon pretty-focus" /></Tooltip>}
												{!!warnings.length && <Tooltip renderContent={() => renderValidationTooltipContent(warnings)} placement="bottom" fallbackPlacements={['bottom-start', 'bottom-end']}><img src={warningIcon} tabIndex={0} aria-label={warnings.join(', ')} alt="Warning" className="create-job-log-table__header-error-icon pretty-focus" /></Tooltip>}
											</div>

											{!!mapping && (mapping.type === 'date' || mapping.type === 'direction') &&
												<div className="mt-2"><a onClick={() => openMappingConfigurationModal(mapping, colIndex)}>Configure</a></div>
											}
										</td>
									);
								}, (colIndex) => (
									<td className={classNames(tableCellClass, 'create-job-log-table__cell_primary-header')} key={`${colIndex}_preview`}> </td>
								))}
							</tr>

							<tr className={classNames('create-job-log-table__row', {'create-job-log-table__row_sticky': !!subHeaderTopStyles })}>
								{renderRowItems(props.callLogData.headers, previewColumnMap, (cell, colIndex) => (
									<td className={classNames(tableCellClass, 'create-job-log-table__cell_secondary-header', { 'create-job-log-table__cell_highlighted': mappingsByColIndex.has(colIndex) })}
										key={colIndex} 
										style={subHeaderTopStyles ?? {}}>
										{mappingsByColIndex.has(colIndex) && <img src={checkmarkBlueIcon} alt="Checkmark" className="create-job-log-table__header-checkmark-icon" />}
										{cell}
									</td>
								), (colIndex) => (
									<td className={classNames(tableCellClass, 'create-job-log-table__cell_preview-header', { 'create-job-log-table__cell_highlighted': mappingsByColIndex.has(colIndex) })}
										key={`${colIndex}_preview`}
										style={subHeaderTopStyles ?? {}}>Data parsed as</td>
								))}
							</tr>

							{callRowsToRender.map((row, rowIndex) => (
								<tr className="create-job-log-table__row" key={rowIndex}>
									{renderRowItems(row, previewColumnMap, (cell, colIndex) => (
										<td className={tableCellClass}
											key={colIndex}>{cell}</td>
									), (colIndex, preview) => {
										const result = getPreviewCallFieldMappingResult(row, colIndex, preview.config);
										return (
											<td className={classNames(tableCellClass, 'create-job-log-table__cell create-job-log-table__cell_preview-cell')}
												key={`${colIndex}_preview`}>
												{!!result && <MappingResultPreview mappingResult={result} separator="bar" />}
											</td>
										);
									})}
								</tr>
							))}
							</tbody>
						</table>
					</div>
				</div>
				{showTruncateMsg() && <div className="create-job-log-table__truncate-msg-section">Preview limited to {maxRowsToRender} calls</div>}
			</div>

			{!!configData &&
			<React.Fragment>
				<MappingConfigurationModal configurationData={configData}
										   mappings={props.mappings}
										   engagementId={props.engagementId}
										   callLogData={props.callLogData}
										   onDone={() => setConfigData(null)} />
			</React.Fragment>
			}
		</React.Fragment>
	);
	
	function isMappingOptionSelectedForColumn(option: MappingSelectOption, columnMapping: CallFieldMappingConfig | undefined, columnIndex: number): boolean {
		if (!columnMapping) return false;
		
		if (option.type === 'participantName') {
			if (columnMapping.type === 'participantName') {
				switch (option.columnType) {
					case ParticipantNameMappingFormat.FirstSpaceLast:
					case ParticipantNameMappingFormat.LastCommaFirst:
						return columnMapping.modeConfig.type === 'singleField' && columnMapping.modeConfig.format === option.columnType;
					case ParticipantNamePart.FirstOnly:
						return columnMapping.modeConfig.type === 'multiField' && columnMapping.modeConfig.firstNameColumnIndex === columnIndex;
					case ParticipantNamePart.LastOnly:
						return columnMapping.modeConfig.type === 'multiField' && columnMapping.modeConfig.lastNameColumnIndex === columnIndex;
				}
			} else {
				return false;
			}		
		} else {
			return option.mapping.id === columnMapping.id;
		}
	}
	
	function isMappingOptionDisabledForColumn(option: MappingSelectOption, columnMapping: CallFieldMappingConfig | undefined, columnIndex: number): boolean {
		if (option.type === 'basic') {
			if (option.mapping.id === columnMapping?.id) return false;

			// mappings are disabled if they are already configured
			switch (option.mapping.type) {
				case 'date':
				case 'filename':
				case 'sessionId':
				case 'basic':
					return option.mapping.columnIndex != null;
				case 'direction':
					return option.mapping.modeConfig.type === 'job' || option.mapping.modeConfig.columnIndex != null;
				case 'participantName':
					// this should never get hit as it gets handled below
					return false;
			}
		} else {
			if (option.mapping.modeConfig.type === 'singleField') {
				if (option.mapping.modeConfig.columnIndex != null) {
					return !(columnIndex === option.mapping.modeConfig.columnIndex && option.mapping.modeConfig.format === option.columnType);
				} else {
					return false;
				}
			} else {
				if (option.mapping.modeConfig.firstNameColumnIndex != null || option.mapping.modeConfig.lastNameColumnIndex != null) {
					switch (option.columnType) {
						case ParticipantNameMappingFormat.FirstSpaceLast:
						case ParticipantNameMappingFormat.LastCommaFirst:
							return true;
						case ParticipantNamePart.FirstOnly:
							return option.mapping.modeConfig.firstNameColumnIndex != null && option.mapping.modeConfig.firstNameColumnIndex !== columnIndex;
						case ParticipantNamePart.LastOnly:
							return option.mapping.modeConfig.lastNameColumnIndex != null && option.mapping.modeConfig.lastNameColumnIndex !== columnIndex;
					}
				} else {
					return false;
				}
			}
		}
	}
	
	function getUpdatedParticipantNameMappingConfig(config: ParticipantNameCallFieldMappingConfig, columnIndex: number, columnType: ParticipantNameColumnType): ParticipantNameCallFieldMappingConfig {
		switch (columnType) {
			case ParticipantNameMappingFormat.FirstSpaceLast:
			case ParticipantNameMappingFormat.LastCommaFirst:
				return {...config, modeConfig: {type: 'singleField', format: columnType, columnIndex}};
			case ParticipantNamePart.FirstOnly:
			case ParticipantNamePart.LastOnly:
				const modeConfig: MultiFieldModeParticipantNameCallFieldMappingConfig = config.modeConfig.type === 'multiField' 
					? config.modeConfig 
					: { type: 'multiField', firstNameColumnIndex: null, lastNameColumnIndex: null };
				
				if (columnType === ParticipantNamePart.FirstOnly) {
					return { 
						...config, 
						modeConfig: { 
							...modeConfig, 
							firstNameColumnIndex: columnIndex, 
							lastNameColumnIndex: modeConfig.lastNameColumnIndex === columnIndex ? null : modeConfig.lastNameColumnIndex 
						}
					};
				} else {
					return { 
						...config, 
						modeConfig: { 
							...modeConfig,
							lastNameColumnIndex: columnIndex, 
							firstNameColumnIndex: modeConfig.firstNameColumnIndex === columnIndex ? null : modeConfig.firstNameColumnIndex 
						}
					};
				}
		}
	}

	function renderValidationTooltipContent(messages: string[]): ReactElement {
		return (
			<React.Fragment>
				{messages.map((m, i) => (<div key={i}>{m}</div>))}
			</React.Fragment>
		)
	}
	
	function openCallDirectionModal(mapping: CallFieldMappingConfig): void {
		if (mapping && mapping.type === 'direction') {
			setConfigData({
				type: 'direction',
				config: mapping
			});
		}
	}
	
	function getSubHeaderTopStyles(height: number | null): { top: string; } | null {
		if (height == null || height <= 0) {
			return null;
		} else {
			return { top: `${height}px` };
		}
	}
	
	function onColumnSelection(idx: number, option: MappingSelectOption | nundefined): void {
		if (!option) {
			dispatch(clearCallFieldMappingByColumnIndex(props.engagementId, idx));
		} else if (option.type === 'participantName') {
			dispatch(updateCallFieldMappingConfig(props.engagementId, getUpdatedParticipantNameMappingConfig(option.mapping, idx, option.columnType)));
		} else {
			const mapping = option.mapping;
			if (mapping) {
				switch (mapping.type) {
					case 'date':
					case 'direction':
						openMappingConfigurationModal(mapping, idx);
						break;
					case 'participantName':
						// this will get caught above
						break;
					default:
						dispatch(updateCallFieldMappingConfig(props.engagementId, { ...mapping, columnIndex: idx }));
						break;
				}
			}
		}
	}

	function openMappingConfigurationModal(mapping: CallFieldMappingConfig, columnIndex: number): void {
		switch (mapping.type) {
			case 'date':
				setConfigData({
					type: 'date',
					config: mapping,
					logColumnIndex: columnIndex
				});
				break;
			case 'direction':
				const modeConfig: CallModeDirectionCallFieldMappingConfig = mapping.modeConfig.type === 'call' && mapping.modeConfig.columnIndex === columnIndex
					? mapping.modeConfig
					: { type: 'call', mappings: new Map(), columnIndex };
				
				setConfigData({
					type: 'direction',
					config: { ...mapping, modeConfig },
					hideModeSelector: true,
					hideColumnSelector: true
				});
				break;
		}
	}
	
	function getMappingsByColIndex(): Map<number, CallFieldMappingConfig> {
		const result: Map<number, CallFieldMappingConfig> = new Map();
		
		props.mappings.forEach(m => {
			getColumnIndexesForMappingConfig(m).forEach(i => {
				result.set(i, m);
			});
		});
		
		return result;
	}
	
	function getPreviewColumnMap(): Map<number, PreviewColumnData> {
		const result: Map<number, PreviewColumnData> = new Map();
		
		props.mappings.forEach(m => {
			if (m.type === 'date' || m.type === 'direction' || (m.type === 'participantName' && m.modeConfig.type === 'singleField')) {
				getColumnIndexesForMappingConfig(m).forEach(idx => {
					result.set(idx, { config: m })
				});
			}
		});
		
		return result;
	}
	
	function renderRowItems(row: string[], previews: Map<number, PreviewColumnData>, renderCell: (item: string, colIndex: number) => ReactElement, renderPreviewCell: (colIndex: number, preview: PreviewColumnData) => ReactElement): ReactElement[] {
		return row.flatMap((item, colIndex) => {
			const cell = renderCell(item, colIndex);
			const preview = previews.get(colIndex);
			
			if (!preview)
				return cell;
			
			return [
				cell,
				renderPreviewCell(colIndex, preview)
			];
		});
	}
	
	function showTruncateMsg(): boolean {
		return rowsAreTruncated;
	}
	
	function getColumnHeaders(columnCount: number): string[] {
		const result: string[] = [];
		for (let i = 0; i < columnCount; i++) {
			result.push('');
		}
		return result;
	}

	function getPreviewCallFieldMappingResult(row: string[], columnIndex: number, config: CallFieldMappingConfig): CallFieldMappingResult | null {
		const result = getCallFieldMappingResult(row, config);
		if (result.type === 'single') {
			return doesMappedCallFieldValueApplyToColumn(result.value, columnIndex) ? result : null;
		} else {
			const applicableValues = result.values.filter(v => doesMappedCallFieldValueApplyToColumn(v, columnIndex));
			return applicableValues.length > 0 ? { type: 'multiple', values: applicableValues } : null;
		}
	}

	function doesMappedCallFieldValueApplyToColumn(value: CallFieldMappingItemResult, columnIndex: number): boolean {
		return !(value.type === 'value' && value.sourceColumnIndex != null && value.sourceColumnIndex !== columnIndex);
	}
	
	function getMappingSelectOptions(): MappingSelectOption[] {
		return props.mappings.flatMap<MappingSelectOption>((m) => {
			const descriptionText = m.requirementType === MappingRequirementType.Required ? 'Required' : null;
			const tooltipText = m.helpText;
			
			if (m.type === 'participantName') {
				const participantTypeDisplayName = m.participantType === 'agent' ? 'Agent' : 'Customer';
				return [
					{ type: 'participantName', id: `${m.id}__${ParticipantNameMappingFormat.FirstSpaceLast}`, columnType: ParticipantNameMappingFormat.FirstSpaceLast, label: `${participantTypeDisplayName} name: First Last`, mapping: m, descriptionText, tooltipText },
					{ type: 'participantName', id: `${m.id}__${ParticipantNameMappingFormat.LastCommaFirst}`, columnType: ParticipantNameMappingFormat.LastCommaFirst, label: `${participantTypeDisplayName} name: Last, First`, mapping: m, descriptionText, tooltipText },
					{ type: 'participantName', id: `${m.id}__${ParticipantNamePart.FirstOnly}`, columnType: ParticipantNamePart.FirstOnly, label: `${participantTypeDisplayName} name: First name only`, mapping: m, descriptionText, tooltipText },
					{ type: 'participantName', id: `${m.id}__${ParticipantNamePart.LastOnly}`, columnType: ParticipantNamePart.LastOnly, label: `${participantTypeDisplayName} name: Last name only`, mapping: m, descriptionText, tooltipText }
				]
			} else {
				return { type: 'basic', id: m.id, label: m.displayName, mapping: m, descriptionText, tooltipText };
			}
		});
	}
}

interface Props {
	engagementId: number;
 	callLogData: CallLogData;
	mappings: CallFieldMappingConfig[];
	showNonIntrusiveErrors: boolean;
}

interface PreviewColumnData {
    config: CallFieldMappingConfig;
}

interface BasicMappingSelectOption extends CustomSelectOption {
	type: 'basic'; id: string; label: string; mapping: CallFieldMappingConfig;
}

interface ParticipantNameSelectOption extends CustomSelectOption {
	type: 'participantName'; 
	columnType: ParticipantNameColumnType; 
	id: string; 
	label: string; 
	mapping: ParticipantNameCallFieldMappingConfig;
}

type MappingSelectOption = BasicMappingSelectOption | ParticipantNameSelectOption;
